import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { row_tooltip, DataTable, Figure, GRID_COLOR, Legend, MUTED_COLOR, Panel, RETAILER_NW_COLOR, WORKING_COLOR, column } from "./trade_review_parts"
import { calendar_month, fmt_day, fmt_int, fmt_month, fmt_pct0, fmt_usd2, fmt_usd_short, funnel_buckets, funnel_row, plan_row, rebuilt_row, trade_review } from "./trade_review_model"

const BUCKET_COLORS: Record<string, string> = {
  "Matched to a calendar promotion": WORKING_COLOR,
  "Real promo, no calendar row": RETAILER_NW_COLOR,
  "Still open": MUTED_COLOR,
}

const FUNNEL_COLUMNS: column<funnel_row>[] = [
  { header: "How it resolved", left: true, cell: (row: funnel_row) => row.step },
  { header: "Amount", cell: (row: funnel_row) => fmt_usd_short(row.amount) },
  { header: "Share", cell: (row: funnel_row) => fmt_pct0(row.share) },
  { header: "What it means", left: true, wrap: true, cell: (row: funnel_row) => row.note },
]

const REBUILT_COLUMNS: column<rebuilt_row>[] = [
  { header: "Retailer", left: true, cell: (row: rebuilt_row) => row.retailer },
  { header: "Start", left: true, cell: (row: rebuilt_row) => fmt_day(row.start) },
  { header: "End", left: true, cell: (row: rebuilt_row) => fmt_day(row.end) },
  { header: "Products", left: true, cell: (row: rebuilt_row) => row.products },
  { header: "Off per unit", cell: (row: rebuilt_row) => (row.rate == null ? "" : fmt_usd2(row.rate)) },
  { header: "Units", cell: (row: rebuilt_row) => fmt_int(row.units) },
  { header: "Billed", cell: (row: rebuilt_row) => fmt_usd_short(row.billed) },
]

const PLAN_COLUMNS: column<plan_row>[] = [
  { header: "Promotion", left: true, cell: (row: plan_row) => row.promo },
  { header: "Retailer", left: true, cell: (row: plan_row) => row.retailer },
  { header: "Plan", cell: (row: plan_row) => fmt_usd_short(row.plan) },
  { header: "Billed", cell: (row: plan_row) => fmt_usd_short(row.billed) },
  { header: "Billed vs plan", cell: (row: plan_row) => fmt_pct0(row.ratio) },
]

type billed_month = calendar_month & { on_calendar: number }

function CalendarCharts({ rows }: { rows: calendar_month[] }) {
  const data: billed_month[] = rows.map((row: calendar_month) => ({ ...row, on_calendar: row.billed - row.billed_no_row }))
  return (
    <Panel title="The calendar stops in June, the billbacks don't" note="Top: KeHE TPRs on the calendar by start month. Bottom: billback dollars by promo start month, split by whether a calendar row exists. Late-year promos may still be billed.">
      <div class="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 16, right: 8, bottom: 0, left: 8 }}>
            <XAxis dataKey="month" hide />
            <YAxis hide />
            <Bar dataKey="kehe_tpr_on_calendar" fill={MUTED_COLOR} radius={[2, 2, 0, 0]} isAnimationActive={false}>
              <LabelList dataKey="kehe_tpr_on_calendar" position="top" fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div class="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="month" tickFormatter={fmt_month} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={fmt_usd_short} width={48} tick={{ fontSize: 12 }} />
            <Tooltip
              cursor={{ fill: "transparent" }}
              content={row_tooltip((row: billed_month) => [fmt_month(row.month), `${fmt_int(row.kehe_tpr_on_calendar)} TPRs on the calendar`, `${fmt_usd_short(row.on_calendar)} billed, on the calendar`, `${fmt_usd_short(row.billed_no_row)} billed, no row`]) as never}
            />
            <Bar dataKey="on_calendar" stackId="billed" fill={WORKING_COLOR} isAnimationActive={false} />
            <Bar dataKey="billed_no_row" stackId="billed" fill={RETAILER_NW_COLOR} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legend items={[{ label: "Billed, on the calendar", color: WORKING_COLOR }, { label: "Billed, no calendar row", color: RETAILER_NW_COLOR }]} />
    </Panel>
  )
}

export function MatchTab({ data }: { data: trade_review }) {
  const s = data.summary
  return (
    <div class="space-y-6">
      <Panel
        title={<>Where <Figure summary={s} field="universe_amt" as="usd_short" /> of KeHE promo deductions landed</>}
        note={<><Figure summary={s} field="universe_n" as="int" /> promo deductions since December 1, 2025, after removing invoices KeHE reversed and rebilled. KeHE fines and PO adjustments are excluded.</>}>
        <div class="flex h-7 w-full gap-0.5" role="img" aria-label="Share of promo deduction dollars by match result">
          {funnel_buckets(data.match_funnel).map((row: { bucket: string; amount: number }) => (
            <div key={row.bucket} class="rounded-sm" title={row.bucket} style={{ flex: row.amount, background: BUCKET_COLORS[row.bucket] }} />
          ))}
        </div>
        <Legend items={Object.entries(BUCKET_COLORS).map(([label, color]: [string, string]) => ({ label, color }))} />
        <DataTable columns={FUNNEL_COLUMNS} rows={data.match_funnel} row_key={(row: funnel_row) => row.step} />
        <p class="text-sm text-muted-foreground">
          Checks: the billed rate equals the calendar rate on all <Figure summary={s} field="rate_n" as="int" /> exact-match lines, and our match agrees with <Figure summary={s} field="gt_agree" as="int" /> of <Figure summary={s} field="gt_n" as="int" /> links your team already made.
        </p>
      </Panel>

      <CalendarCharts rows={data.calendar_months} />

      <div class="grid gap-4 xl:grid-cols-2">
        <Panel title="Promos we rebuilt from billbacks" note={<><Figure summary={s} field="rebuilt_n" as="int" /> promos from mid-June on, worth <Figure summary={s} field="rebuilt_amt" as="usd_short" />. Each line gives the retailer, dates, items, rate and units, ready to review and load.</>}>
          <DataTable tall columns={REBUILT_COLUMNS} rows={data.rebuilt_promos} row_key={(row: rebuilt_row) => row.promo} />
        </Panel>
        <Panel title="Plan against billed, by TPR" note={<>Billed excludes KeHE's processing fee, so compare it with the allowance in the plan. <Figure summary={s} field="within_n" as="int" /> of <Figure summary={s} field="plan_n" as="int" /> landed within 25% of plan.</>}>
          <DataTable tall columns={PLAN_COLUMNS} rows={data.plan_vs_billed} row_key={(row: plan_row) => row.key} />
        </Panel>
      </div>
    </div>
  )
}
